import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { Ripple } from "primereact/ripple";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputSwitch } from "primereact/inputswitch";
import { InputNumberChangeEvent } from "primereact/inputnumber";
import { confirmDialog } from "primereact/confirmdialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tooltip } from "primereact/tooltip";
import { Checkbox, CheckboxChangeEvent } from "primereact/checkbox";

import NoDataCreated from "../../../../Components/NoDataCreated";
import ErrorPage from "../../../../Components/ErrorPage";
import PageLoader from "../../../../Components/PageLoader";
import NumberInput from "../../../../Components/NumberInput";
import { goToTop } from "../../../../Components/goToTop";
import { baseURL } from "../../../../Api/ApiConstant";
import { amountDisplay, getAppCurrencyType } from "../../../../Utils/Common";
import { checkModelSelected, checkNumber, checkText } from "../../../../Utils/Validator";

import { SiteEmployeeType } from "../../../../Controllers/Types/HeyTraderTypes/Attendances/SiteEmployeeType";
import { ContactEmployeeType } from "../../../../Controllers/Types/HeyTraderTypes/Attendances/ContactEmployeeType";
import { useAppDispatch, useAppSelector } from "../../../../Controllers/hooks";
import {
  getAttendanceEmployeeList,
  getContactEmployeeList,
} from "../../../../Controllers/Thunks/HeyTraderThunks";
import { AttendanceProjectType } from "../../../../Controllers/Types/HeyTraderTypes/Attendances/AttendanceProjectType";
import { activeAttendancesEmployeeNew, addAttendancesEmployeeNew, updateAttendancesEmployeeNew } from "../../../../Api/NewAttendance/NewAttendanceService";

const SiteEmployees: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currencySymbol = getAppCurrencyType();
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState<boolean>(false);
  const [initialDataLoading, setInitialDataLoading] = useState<boolean>(true);
  const toast = useRef<Toast>(null);
  const [dataState, setDataState] = useState<"Add" | "Edit">("Add");

  /* Add employee */
  const [showEmployeeModal, setShowEmployeeModal] = useState<boolean>(false);
  const [dailyHours, setDailyHours] = useState<number | null>(null);
  const [dailyWage, setDailyWage] = useState<number | null>(null);
  const [payType, setPayType] = useState<'Day' | 'Hour'>("Day");

  const [contactEmployeeError, setContactEmployeeError] = useState<string>("");
  const [dailyHoursError, setDailyHoursError] = useState<string>("");
  const [dailyWageError, setDailyWageError] = useState<string>("");
  const [payTypeError, setPayTypeError] = useState<string>('');

  const [contactEmployeesData, setContactEmployeesData] = useState<ContactEmployeeType[]>([]);
  const [filteredContactEmployeesData, setFilteredContactEmployeesData] = useState<ContactEmployeeType[]>([]);
  const [selectedContactEmployee, setSelectedContactEmployee] = useState<ContactEmployeeType | null>(null);
  const [showContactEmployeesListModal, setShowContactEmployeesListModal] = useState<boolean>(false);
  const [employeeSearchKey, setEmployeeSearchKey] = useState<string>("");

  const [siteEmployeesData, setSiteEmployeesData] = useState<SiteEmployeeType[]>([]);
  const [selectedSiteEmployeeData, setSelectedSiteEmployeeData] = useState<SiteEmployeeType | null>(null);
  const [employeeStatusChangeLoadingId, setEmployeeStatusChangeLoadingId] = useState<number | null>(null);
  const [updateHasChanges, setUpdateHasChanges] = useState<boolean>(false);

  const [mainProject, setMainProject] = useState<AttendanceProjectType | null>(null);

  const dataLoading = useAppSelector((state) => state?.attendanceEmployees?.loader);
  const dataError = useAppSelector((state) => state?.attendanceEmployees?.errorDisplay);
  const contacEmployeetList = useAppSelector((state) => state.contactEmployees.contactEmployeeList);
  const siteEmployeeList = useAppSelector((state) => state.attendanceEmployees.attendanceEmployeeList);

  useEffect(() => {
    handleEmployeesDataGet();
    dispatch(getContactEmployeeList());
  }, []);

  const handleEmployeesDataGet = async () => {
    await dispatch(getAttendanceEmployeeList());

    setInitialDataLoading(false);
  }

  useEffect(() => {
    if (location?.state && location?.state?.mainProject) {
      setMainProject(location?.state?.mainProject);
    } else {
      setMainProject(null);
    }
  }, [location?.state]);

  useEffect(() => {
    const fetchData = async () => {
      setContactEmployeesData(contacEmployeetList);
    };
    fetchData();
  }, [contacEmployeetList]);

  useEffect(() => {
    const fetchData = async () => {
      setSiteEmployeesData(siteEmployeeList);
    };
    fetchData();
  }, [siteEmployeeList]);

  useEffect(() => {
    if (contactEmployeesData && contactEmployeesData.length > 0) {
      const searchKey = (employeeSearchKey || "").toLowerCase();

      const filteredEmployees = contactEmployeesData.filter((employee) => {
        const firstName = employee?.personalInfo?.first_name || "";
        const lastName = employee?.personalInfo?.last_name || "";

        return (
          firstName.toLowerCase().includes(searchKey) ||
          lastName.toLowerCase().includes(searchKey)
        );
      });

      setFilteredContactEmployeesData(filteredEmployees);
    } else {
      setFilteredContactEmployeesData([]);
    }
  }, [contactEmployeesData, employeeSearchKey]);


  /* ********************************* Employee ******************************** */
  const handleCloseEmployeeModal = () => {
    setShowEmployeeModal(false);
    handleResetEmployeeFields();
    handleClearEmployeeErrors();
  };

  const handleResetEmployeeFields = () => {
    setSelectedContactEmployee(null);
    setPayType('Day');
    setDailyHours(null);
    setDailyWage(null);
  };

  const handleClearEmployeeErrors = () => {
    setContactEmployeeError("");
    setPayTypeError("");
    setDailyHoursError("");
    setDailyWageError("");
  };

  const handleAddEmployee = () => {
    setDataState("Add");
    setShowEmployeeModal(true);
  };

  // Clear errors
  useEffect(() => {
    checkModelSelected(selectedContactEmployee !== null, false, setContactEmployeeError);
  }, [selectedContactEmployee]);

  useEffect(() => {
    checkText(payType, false, setPayTypeError);
  }, [payType]);

  useEffect(() => {
    checkNumber(dailyHours!, false, setDailyHoursError);
  }, [dailyHours]);

  useEffect(() => {
    checkNumber(dailyWage!, false, setDailyWageError);
  }, [dailyWage]);

  // Check validations
  function checkCredentials(): boolean {
    if (dataState === "Add") {
      const contact = checkModelSelected(selectedContactEmployee !== null, true, setContactEmployeeError);
      const type = checkText(payType, true, setPayTypeError);
      const hours = checkNumber(dailyHours!, (payType === 'Hour' ? true : false), setDailyHoursError);
      const wage = checkNumber(dailyWage!, true, setDailyWageError);

      return contact && type && hours && wage;
    } else {
      const type = checkText(payType, true, setPayTypeError);
      const hours = checkNumber(dailyHours!, (payType === 'Hour' ? true : false), setDailyHoursError);
      const wage = checkNumber(dailyWage!, true, setDailyWageError);

      return type && hours && wage;
    }
  }

  // API validation
  function showAPIError(keyName: string, error: string): void {
    if (keyName === "personal_info_id") {
      setContactEmployeeError(error);
    }

    if (keyName === "work_type") {
      setPayTypeError(error);
    }

    if (keyName === "daily_hours") {
      setDailyHoursError(error);
    }

    if (keyName === "daily_wage") {
      setDailyWageError(error);
    }
  }

  //   add employee details
  const handleCreateEmployee = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!checkCredentials()) return;

    setLoading(true);

    try {
      const personal_info_id = selectedContactEmployee?.personalInfo?.id ?? null;
      const work_type = payType;
      const daily_hours = payType === 'Hour' ? (dailyHours ?? 0) : null;
      const daily_wage = dailyWage ?? 0;

      const data = await addAttendancesEmployeeNew(
        personal_info_id,
        work_type,
        daily_hours,
        daily_wage
      );

      if (data.statusCode === 200) {
        // call api
        handleEmployeesDataGet();
        dispatch(getContactEmployeeList());

        handleCloseEmployeeModal();

        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Employee added successfully.",
            life: 3000,
          });
        }
      } else if (data.statusCode === 422) {
        Object.entries(data.errors ?? []).map(([key, value]) => (
          showAPIError(key, value[0])
        ));
      } else {
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Failed",
            detail: "There was an error adding the employee.",
            life: 3000,
          });
        }
      }
    } catch (error) {
      console.error("Error adding the employee:", error);

      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Something went wrong while adding the employee.",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedSiteEmployeeData) {
      setUpdateHasChanges(false);
      return;
    }

    const workType = selectedSiteEmployeeData?.attenanceEmployee?.work_type;

    const hasChanges =
      payType !== workType ||
      (workType === 'Hour' && dailyHours !== selectedSiteEmployeeData?.attenanceEmployee?.daily_hours) ||
      dailyWage !== selectedSiteEmployeeData?.attenanceEmployee?.daily_wage;

    setUpdateHasChanges(hasChanges);
  }, [
    selectedSiteEmployeeData,
    payType,
    dailyHours,
    dailyWage
  ])

  const handleEditEmployee = (data: SiteEmployeeType) => {
    if (data && data.attenanceEmployee) {
      setSelectedSiteEmployeeData(data);
      setDataState("Edit");
      setShowEmployeeModal(true);

      setPayType(data?.attenanceEmployee?.work_type);
      setDailyHours(data?.attenanceEmployee?.work_type === 'Hour' ? (data?.attenanceEmployee?.daily_hours ?? null) : null);
      setDailyWage(data?.attenanceEmployee?.daily_wage ?? null);
    }
  };

  const handleUpdateEmployee = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!checkCredentials() || selectedSiteEmployeeData === null) return;

    setLoading(true);

    try {
      const work_type = payType;
      const daily_hours = payType === 'Hour' ? (dailyHours ?? 0) : null;
      const daily_wage = dailyWage ?? 0;
      const id = selectedSiteEmployeeData?.attenanceEmployee?.id ?? null;

      const data = await updateAttendancesEmployeeNew(
        work_type,
        daily_hours,
        daily_wage,
        id
      );

      if (data.statusCode === 200) {
        // call api
        handleEmployeesDataGet();

        handleCloseEmployeeModal();

        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Employee updated successfully.",
            life: 3000,
          });
        }
      } else if (data.statusCode === 422) {
        Object.entries(data.errors ?? []).map(([key, value]) => (
          showAPIError(key, value[0])
        ));
      } else {
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Failed",
            detail: "There was an error updating the employee.",
            life: 3000,
          });
        }
      }
    } catch (error) {
      console.error("Error updating the employee:", error);

      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Something went wrong while updating the employee.",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const employeeModalHeader = (
    <div className="custom_modal_header_inner">
      <h5 className="modal-title fs-5">{dataState} employee</h5>
      <button
        type="button"
        className="close_modal_btn p-ripple"
        onClick={handleCloseEmployeeModal}
      >
        <i className="bi bi-x-circle"></i>
        <Ripple />
      </button>
    </div>
  );

  const employeeModalFooter = (
    <div className="custom_modal_footer">
      <Button
        label="Close"
        className="custom_btn secondary"
        onClick={handleCloseEmployeeModal}
      />

      <Button
        label={`${loading ? "Processing" : dataState === "Add" ? "Save" : "Update"}`}
        onClick={dataState === "Add" ? handleCreateEmployee : handleUpdateEmployee}
        loading={loading}
        disabled={dataState === 'Edit' && !updateHasChanges}
        className="custom_btn primary"
      />
    </div>
  );

  const employeeProfileBody = (rowData: SiteEmployeeType) => {
    const info = rowData?.personalInfo;

    const profileImage = info?.profile_image;
    const firstName = info?.first_name ? info?.first_name : "";
    const lastName = info?.last_name ? info?.last_name : "";
    const profileNameLetters = firstName[0] + lastName[0];
    const fullName = info
      ? `${info.first_name ?? '---'}${info.last_name ? ' ' + info.last_name : ''}`
      : '---';

    return (
      <div className="employee_profile_display justify-content-start gap-3">
        <div className="employee_profile_image">
          {profileImage !== null ? (
            <img src={baseURL + profileImage} alt="Employee" />
          ) : (
            <span>{profileNameLetters}</span>
          )}
        </div>

        <div>
          <span className="employee_name">{fullName}</span>
          {info?.email && <small className="text_muted d-block">{info.email}</small>}
        </div>
      </div>
    );
  };

  const employeeStatusBody = (rowData: SiteEmployeeType) => {
    return (
      <div className="input_switch_area">
        {employeeStatusChangeLoadingId === rowData?.attenanceEmployee?.personal_info_id ? (
          <ProgressSpinner
            className="table_action_loader"
            style={{ width: '25px', height: '25px' }}
            strokeWidth="8"
            fill="transparent"
            animationDuration="1s"
          />
        ) : (
          <InputSwitch
            checked={rowData?.attenanceEmployee?.status === 1}
            onChange={(e) => handleChangeEmployeeStatus(rowData)}
          />
        )}
      </div>
    );
  };

  const tableActionBody = (rowData: SiteEmployeeType) => {
    return (
      <div className="table_action_btn_area">
        <button
          className="table_action_btn p-ripple"
          onClick={() => handleEditEmployee(rowData)}
        >
          Edit
          <Ripple />
        </button>
      </div>
    );
  };

  const handleViewContactEmployeesList = () => {
    setShowContactEmployeesListModal(true);
  };

  const handleCloseContactEmployeesListModal = () => {
    setShowContactEmployeesListModal(false);
  };

  const handleSelectEmployee = (data: ContactEmployeeType) => {
    if (data) {
      setSelectedContactEmployee(data);
      setTimeout(() => {
        handleCloseContactEmployeesListModal();
      }, 200);
    } else {
      setSelectedContactEmployee(null);
    }
  };

  const handleDailyHoursChange = (e: InputNumberChangeEvent) => {
    if (e.value != null) {
      setDailyHours(e.value);
    } else {
      setDailyHours(null);
    }
  };

  const handleDailyWageChange = (e: InputNumberChangeEvent) => {
    if (e.value != null) {
      setDailyWage(e.value);
    } else {
      setDailyWage(null);
    }
  };

  const handleChangeEmployeeStatus = (rowData: SiteEmployeeType) => {
    confirmDialog({
      message: "Are you sure you want to change the employee's status?",
      header: "Confirm Status Change",
      icon: "bi bi-info-circle",
      defaultFocus: "accept",
      dismissableMask: true,
      resizable: false,
      draggable: false,
      accept: () => updateEmployeeStatus(rowData),
    });
  };

  const updateEmployeeStatus = async (employee: SiteEmployeeType) => {
    if (!employee) return;

    setEmployeeStatusChangeLoadingId(employee?.attenanceEmployee?.personal_info_id ?? null);

    try {
      const result = await activeAttendancesEmployeeNew(
        employee.attenanceEmployee?.id!
      );

      if (result.statusCode === 200) {
        handleEmployeesDataGet();

        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Employee status updated successfully.",
            life: 3000,
          });
        }
      } else {
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Failed",
            detail: `There was an error updating the employee status.`,
            life: 3000,
          });
        }
      }
    } catch (error) {
      console.error("Error updating the employee status:", error);

      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Something went wrong while updating the employee status.",
        life: 3000,
      });
    } finally {
      setEmployeeStatusChangeLoadingId(null);
    }
  };
  /* *************************************************************************** */

  /* ***************************** Common function ***************************** */
  const goBack = () => {
    navigate(-1);
    goToTop();
  };
  /* *************************************************************************** */

  return (
    <React.Fragment>
      <Toast ref={toast} />

      <div className="main_content layout_padding show_side_bar">
        {dataLoading && initialDataLoading ? (
          <PageLoader />
        ) : dataError ? (
          <ErrorPage errorMessage={dataError} />
        ) : (
          <div className="page_content mt-0">
            <div className="page_breadcrumb_area">
              <ul className="page_breadcrumb">
                {mainProject && mainProject !== null && (
                  <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-2)}>
                    {mainProject?.job_number}: {mainProject?.address}
                  </li>
                )}

                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                  Attendance
                </li>
                <li className="page_breadcrumb_item active">
                  Site Employees
                </li>
              </ul>

              <button className="back_btn p-ripple" onClick={goBack}>
                <i className="bi bi-arrow-left me-2"></i>
                Back
                <Ripple />
              </button>
            </div>

            <div className="page_header_area justify-content-end mt-sm-3 mt-3">
              <div className="page_action_area">
                <button
                  className="add_data_link_btn p-ripple"
                  onClick={handleAddEmployee}
                >
                  <i className="bi bi-plus-circle"></i>
                  <span>Add employee</span>
                  <Ripple />
                </button>
              </div>
            </div>

            <div className="page_main_content_section">
              {siteEmployeesData && siteEmployeesData?.length > 0 ? (
                <DataTable
                  value={siteEmployeesData}
                  paginator
                  size="small"
                  rows={10}
                  emptyMessage="No data found!"
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  tableStyle={{ minWidth: "50rem" }}
                  paginatorDropdownAppendTo={"self"}
                  paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                  currentPageReportTemplate="{first} to {last} of {totalRecords}"
                  className="page_table p-0 p-sm-2"
                  loading={dataLoading}
                  dataKey="attenanceEmployee.personal_info_id"
                  rowHover
                  scrollable
                >
                  <Column
                    header="Employee"
                    headerClassName="text_purple"
                    body={employeeProfileBody}
                    style={{ width: "25%" }}
                  ></Column>

                  <Column
                    header="Designation"
                    headerClassName="text_purple"
                    field="userRoleInfo.role_name"
                    body={(rowData: SiteEmployeeType) => (
                      <span className="text-capitalize">
                        {rowData?.userRoleInfo?.role_name ? rowData?.userRoleInfo?.role_name : "---"}
                      </span>
                    )}
                    style={{ width: "20%" }}
                  ></Column>

                  <Column
                    header="Type"
                    alignHeader={"center"}
                    headerClassName="text_purple"
                    field="attenanceEmployee.work_type"
                    body={(rowData: SiteEmployeeType) => (
                      <span className="text-center flex_center">
                        {rowData?.attenanceEmployee?.work_type ? rowData?.attenanceEmployee?.work_type : "---"}
                      </span>
                    )}
                    style={{ width: "10%" }}
                  ></Column>

                  <Column
                    header="Hours"
                    alignHeader={"center"}
                    headerClassName="text_purple"
                    field="attenanceEmployee.daily_hours"
                    body={(rowData: SiteEmployeeType) => (
                      <span className="text-center flex_center">
                        {rowData?.attenanceEmployee?.daily_hours !== null ? `${rowData?.attenanceEmployee?.daily_hours} hrs` : "---"}
                      </span>
                    )}
                    style={{ width: "10%" }}
                  ></Column>

                  <Column
                    header="Wage/Day"
                    alignHeader={"right"}
                    headerClassName="text_purple"
                    field="attenanceEmployee.daily_wage"
                    body={(rowData: SiteEmployeeType) => (
                      <span className="text-end flex_end">
                        {rowData?.attenanceEmployee?.daily_wage !== null ? amountDisplay(rowData?.attenanceEmployee?.daily_wage ?? 0) : "---"}
                      </span>
                    )}
                    style={{ width: "15%" }}
                  ></Column>

                  <Column
                    alignHeader={"center"}
                    body={employeeStatusBody}
                    style={{ width: "10%" }}
                  ></Column>

                  <Column
                    body={tableActionBody}
                    style={{ width: "10%" }}
                    frozen
                    alignFrozen="right"
                  ></Column>
                </DataTable>
              ) : (
                <NoDataCreated message={`No employee(s) found!`} />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Employee modal */}
      <Dialog
        visible={showEmployeeModal}
        header={employeeModalHeader}
        footer={employeeModalFooter}
        headerClassName="custom_modal_header"
        className="custom_modal_dialog modal_dialog_sm"
        onHide={handleCloseEmployeeModal}
        dismissableMask
      >
        <div className="custom_modal_body">
          <div className="p-1 pt-2 p-sm-4 pt-sm-2 pb-sm-2 w-100">
            <div className="row">
              {/* Employee */}
              {dataState === "Add" ? (
                <React.Fragment>
                  <div className="col-12">
                    <div className="page_form_group">
                      <label htmlFor="employee" className="custom_form_label is_required">
                        Employee
                      </label>
                      <div className="input_with_button_area">
                        <InputText
                          key={"employee"}
                          id="employee"
                          value={selectedContactEmployee && (selectedContactEmployee?.personalInfo?.first_name || "") + " " + (selectedContactEmployee?.personalInfo?.last_name || "")}
                          className="custom_form_input has_add_value capitalized_input"
                          placeholder="Click to select the employee."
                          readOnly={true}
                          autoComplete="off"
                          onClick={handleViewContactEmployeesList}
                        />
                        <button
                          className="add_value_btn p-ripple"
                          onClick={handleViewContactEmployeesList}
                        >
                          {selectedContactEmployee === null ? (
                            <React.Fragment>
                              <i className="bi bi-plus-lg"></i>
                              <span>Add</span>
                            </React.Fragment>
                          ) : (
                            <React.Fragment>
                              <i className="bi bi-pencil-square"></i>
                              <span>Change</span>
                            </React.Fragment>
                          )}
                          <Ripple />
                        </button>
                      </div>

                      {contactEmployeeError && (
                        <small className="form_error_msg">
                          {contactEmployeeError}
                        </small>
                      )}
                    </div>
                  </div>
                </React.Fragment>
              ) : dataState === "Edit" ? (
                <div className="col-12">
                  <div className="page_form_group">
                    <label htmlFor="employeeName" className="custom_form_label">
                      Employee name
                    </label>
                    <InputText
                      key={"employeeName"}
                      id="employeeName"
                      value={selectedSiteEmployeeData && (selectedSiteEmployeeData?.personalInfo?.first_name || "") + " " + (selectedSiteEmployeeData?.personalInfo?.last_name || "")}
                      className="custom_form_input text-capitalize"
                      placeholder="Employee name"
                      readOnly={true}
                      autoComplete="off"
                      disabled
                    />
                  </div>
                </div>
              ) : null}

              {/* Pay type */}
              <div className="col-12">
                <div className="page_form_group">
                  <label htmlFor="payType" className="custom_form_label is_required">
                    Pay type
                  </label>

                  <div className="response_state_area">
                    <div className="response_state">
                      <Checkbox
                        inputId="Day"
                        name="payType"
                        value="Day"
                        onChange={(e: CheckboxChangeEvent) => setPayType(e.value)}
                        checked={payType === "Day"}
                      />
                      <label htmlFor="Day">
                        Day
                      </label>
                    </div>

                    <div className="response_state">
                      <Checkbox
                        inputId="Hour"
                        name="payType"
                        value="Hour"
                        onChange={(e: CheckboxChangeEvent) => setPayType(e.value)}
                        checked={payType === "Hour"}
                      />
                      <label htmlFor="Hour">
                        Hour
                      </label>
                    </div>
                  </div>

                  {payTypeError && (
                    <small className="form_error_msg">{payTypeError}</small>
                  )}
                </div>
              </div>

              {/* Daily hour(s) */}
              {payType === 'Hour' && (
                <div className="col-12">
                  <NumberInput
                    inputId="dailyHours"
                    label="Daily hour(s)"
                    labelHtmlFor="dailyHours"
                    placeholder="Daily hour(s)"
                    onChange={handleDailyHoursChange}
                    value={dailyHours}
                    required={true}
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    suffix=" hour(s)"
                    min={0}
                    error={dailyHoursError}
                  />
                </div>
              )}

              {/* Wage */}
              <div className="col-12">
                <NumberInput
                  inputId="dailyWage"
                  label="Daily wage"
                  labelHtmlFor="dailyWage"
                  placeholder="Daily wage"
                  onChange={handleDailyWageChange}
                  value={dailyWage}
                  required={true}
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  prefix={currencySymbol + " "}
                  min={0}
                  error={dailyWageError}
                  formGroupClassName="mb-2"
                />
              </div>
            </div>
          </div>
        </div>
      </Dialog>
      {/*  */}

      {/* Contact employees */}
      <Dialog
        visible={showContactEmployeesListModal}
        headerClassName="d-none"
        dismissableMask
        className="custom_modal_dialog modal_dialog_sm data_select_modal"
        contentClassName="data_select_modal_content"
        onHide={handleCloseContactEmployeesListModal}
      >
        <div className="custom_modal_body data_select_modal_area">
          <div className="data_select_modal_header">
            <div className="data_search_input_area">
              <InputText
                key={"employeeSearchKey"}
                id="employeeSearchKey"
                value={employeeSearchKey}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmployeeSearchKey(e.target.value)}
                className="custom_form_input data_search_input"
                placeholder="Search employee by name"
                autoComplete="off"
                autoFocus
                type={"search"}
              />
              {employeeSearchKey === "" ? (
                <i className="bi bi-search"></i>
              ) : (
                <i
                  className="bi bi-x-lg data_search_input_clear"
                  onClick={() => setEmployeeSearchKey("")}
                ></i>
              )}
            </div>

            <button
              type="button"
              className="data_select_modal_close p-ripple"
              onClick={handleCloseContactEmployeesListModal}
            >
              <i className="bi bi-x"></i>
              <Ripple />
            </button>
          </div>

          <div className="data_select_modal_body has_custom_input">
            {(filteredContactEmployeesData && filteredContactEmployeesData?.length > 0) ? (
              <div className="data_select_option_area">
                {filteredContactEmployeesData?.map((employee) => {
                  const profileImage = employee?.personalInfo?.profile_image || "";
                  const firstName = employee?.personalInfo?.first_name || "";
                  const lastName = employee?.personalInfo?.last_name || "";
                  const roleName = employee?.userRoleInfo?.display_role_name || "";
                  const contactNumber = employee?.personalInfo?.mobile || "";
                  const fullName = firstName + " " + lastName;
                  const profileLetter = firstName[0] + lastName[0];

                  const subType = employee?.contact_subtype;
                  const subTypeShort = subType === 'Employee' ? 'PE' : subType === 'Temporary Employee' ? 'TE' : '';

                  return (
                    <React.Fragment>
                      <Tooltip
                        key={`emp_type${employee.contact_id}`}
                        id={`emp_type${employee.contact_id}`}
                        target={`.emp_type${employee.contact_id}`}
                      />

                      <div
                        key={employee?.contact_id}
                        className={`contact_select_option ${selectedContactEmployee?.contact_id === employee?.contact_id && "selected"} p-ripple`}
                        onClick={() => handleSelectEmployee(employee)}
                      >
                        <div className="contact_prof_avatar">
                          {profileImage ? (
                            <img src={baseURL + profileImage} alt={fullName} />
                          ) : (
                            <span>{profileLetter}</span>
                          )}
                        </div>
                        <div className="contact_prof_info">
                          <h6>{fullName}</h6>
                          <p>
                            <span>{roleName}</span>
                            <span className="text_light">
                              {(roleName && contactNumber) && ' | '}
                            </span>
                            <span>{contactNumber}</span>
                            <span className="text_light">&nbsp;|&nbsp;</span>
                            <span
                              className={`emp_type${employee?.contact_id}`}
                              data-pr-tooltip={subTypeShort === 'PE' ? 'Permanent Employee' : 'Temporary Employee'}
                              data-pr-position="top"
                            >
                              {subTypeShort}
                            </span>
                          </p>
                        </div>
                        <Ripple
                          pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" }, }, }}
                        />
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            ) : (
              <div className="no_records_found">
                <i className="bi bi-exclamation-circle me-1"></i>
                No contact(s) found.
              </div>
            )}
          </div>

          <div className="page_form_group data_select_modal_footer with_link mb-0">
            <Button
              label={`Add more employees`}
              icon="bi bi-box-arrow-up-right"
              iconPos="right"
              onClick={() => {
                navigate(`/contact`);
                goToTop();
              }}
              className="custom_btn primary pt-2 pb-2 link_btn"
            />
          </div>
        </div>
      </Dialog>
    </React.Fragment>
  );
};

export default SiteEmployees;
